<script setup>
import {getCurrentInstance, ref} from 'vue'
import helpers from '@/lib/helpers'
import LeadLinkModal from '@/components/event/lead-link-modal.vue'
import ActivityTimeline from '@/components/activity/activity-timeline.vue'

const props = defineProps({
  lead: Object,
  url: String
})

const proxy = getCurrentInstance().proxy
const record = ref(props.lead || null)

const refresh = async () => {
  if (props.url)
    record.value = await helpers.asyncAction(props.url, 'GET')
}

refresh()

const openLink = (type) => {
  proxy.$dialog.showAndWait(LeadLinkModal, {lead: record.value, type}).then(res => {
    if (res) record.value = res
    else refresh()
  })
}

const unlink = (side) => {
  helpers.asyncAction(side.unlink_url.href, side.unlink_url.method).then(() => refresh())
}
</script>

<template>
  <modal :title="'Lead Details' + (record ? ' - ' + record.gli : '')" large height="560px" no-scroll>
    <b-tabs card v-if="record">
      <b-tab title="Details" active>
        <b-container fluid class="py-2">
          <dl class="row mb-2">
            <dt class="col-4">GLI</dt>
            <dd class="col-8">{{ record.gli }}</dd>

            <dt class="col-4">Event</dt>
            <dd class="col-8">
              <b-link @click="global_lookup(record.event_gli, true)">{{ record.event_gli }}</b-link>
            </dd>

            <dt class="col-4">Name</dt>
            <dd class="col-8">{{ record.name || '-' }}</dd>

            <dt class="col-4">Status</dt>
            <dd class="col-8">
              {{ record.status_desc || '-' }}
              <b-badge v-if="record.status === 'D'" variant="dark" class="ml-1">Duplicate</b-badge>
            </dd>

            <template v-if="record.duplicate_of && record.duplicate_of.length">
              <dt class="col-4 text-danger">Duplicate of</dt>
              <dd class="col-8">
                <div v-for="d in record.duplicate_of" :key="d.id" class="small">
                  <b-link @click="global_lookup(d.gli, true)">{{ d.gli }}</b-link>
                  <span v-if="d.name"> ({{ d.name }})</span>
                  <span v-if="d.event" class="text-muted"> — {{ d.event.name || d.event.gli }}</span>
                  <span class="text-muted"> [{{ (d.matched_on || []).join(' & ') }}]</span>
                </div>
              </dd>
            </template>

            <dt class="col-4">Company</dt>
            <dd class="col-8">{{ record.company || '-' }}</dd>

            <dt class="col-4">Title</dt>
            <dd class="col-8">{{ record.title || '-' }}</dd>

            <dt class="col-4">Email</dt>
            <dd class="col-8">
              <b-link v-if="record.email" :href="'mailto:' + record.email">{{ record.email }}</b-link>
              <template v-else>-</template>
            </dd>

            <dt class="col-4">Phone</dt>
            <dd class="col-8">{{ record.phone || '-' }}</dd>

            <dt class="col-4">Source</dt>
            <dd class="col-8">{{ record.source || '-' }}</dd>

            <dt class="col-4">More Info</dt>
            <dd class="col-8">{{ record.more_info || '-' }}</dd>

            <dt class="col-4">Location</dt>
            <dd class="col-8">
              {{ [record.city, record.state, record.zip].filter(Boolean).join(', ') || '-' }}
              <span v-if="record.country" class="text-muted"> ({{ record.country }})</span>
            </dd>

            <dt class="col-4">Do Not Contact</dt>
            <dd class="col-8">
              <b-badge v-if="record.do_not_contact" variant="danger">Do Not Contact</b-badge>
              <template v-else>No</template>
            </dd>

            <dt class="col-4">Created</dt>
            <dd class="col-8">{{ record.created | dateonly }}
              <b-badge v-if="record.createdby_user" class="ml-1">{{ record.createdby_user.userid }}</b-badge>
            </dd>

            <template v-for="side in ['customer', 'vendor']">
              <dt class="col-4" :key="side + '-l'">{{ side === 'customer' ? 'Customer' : 'Vendor' }}</dt>
              <dd class="col-8" :key="side + '-v'">
                <template v-if="record.links && record.links[side]">
                  <b-link v-if="record.links[side].can_view" @click="global_lookup(record.links[side].gli, true)">
                    {{ record.links[side].label || record.links[side].gli }}
                  </b-link>
                  <span v-else>{{ record.links[side].label || record.links[side].gli }}</span>
                  <b-badge v-if="record.links[side].auto" variant="light" class="ml-1">auto</b-badge>
                  <b-button v-if="can('events.leads.write')" size="sm" variant="link" class="p-0 ml-2"
                            @click="openLink(side)">change</b-button>
                  <b-button v-if="can('events.leads.write')" size="sm" variant="link" class="p-0 ml-2 text-danger"
                            @click="unlink(record.links[side])">unlink</b-button>
                </template>
                <b-button v-else-if="can('events.leads.write')" size="sm" variant="link" class="p-0"
                          @click="openLink(side)">
                  <font-awesome-icon :icon="['fas', 'link']" class="mr-1"/>Link / create
                </b-button>
                <span v-else>-</span>
              </dd>
            </template>
          </dl>
        </b-container>
      </b-tab>

      <b-tab title="Imported" v-if="record.imported && Object.keys(record.imported).length">
        <b-container fluid class="py-2">
          <p class="text-muted small mb-2">The original row as it came in during import. Read-only.</p>
          <b-table-simple small hover class="mb-0">
            <b-tbody>
              <b-tr v-for="(v, k) in record.imported" :key="k">
                <b-th class="text-nowrap align-top" style="width: 30%">{{ k }}</b-th>
                <b-td class="text-break">{{ (v === null || v === '') ? '—' : (typeof v === 'object' ? JSON.stringify(v) : v) }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-container>
      </b-tab>

      <b-tab title="Activity" v-if="record._links && record._links.activity_url" lazy>
        <b-container fluid class="py-2">
          <activity-timeline :url="record._links.activity_url.href" write="events.leads.notes.write" height="400px"/>
        </b-container>
      </b-tab>

      <b-tab title="Files">
        <files standalone :url="record._links.file_url.href" height="380px"/>
      </b-tab>
    </b-tabs>

    <template #footer>
      <b-btn size="sm" variant="outline-secondary" class="px-3" @click="$root.$emit('submit')">Close</b-btn>
    </template>
  </modal>
</template>

<style scoped>
dt {
  font-weight: 500;
}
</style>
